<template>
  <a-popover
    v-bind="attrs"
    v-model:open="newOpen"
    :arrow="false"
    trigger="click"
    :placement="placement"
    :overlay-class-name="`${bem.b()}`"
  >
    <ll-icon-base-pro
      :class="`${bem.e('icon')} ${bem.is(newOpen ? 'active' : '')}`"
      :show-tip="false"
      :icon-name="icon || 'icon-set-line'"
      bg-color="var(--color-fill-tertiary)"
      hover-bg-color="var(--color-fill-secondary)"
      size="large"
      :icon-size="16"
    />
    <template #content>
      <div
        :class="bem.e('content')"
        :style="{
          minWidth: minWidth ? addUnit(minWidth) : '180px',
        }"
      >
        <a-form-item-rest>
          <draggable
            v-model="columnList"
            :animation="200"
            :class="bem.e('list')"
            :style="{
              maxHeight: maxHeight ? addUnit(maxHeight) : '300px',
            }"
            item-key="key"
            handle=".handle"
            @start="startDrag"
            @update="updateDrag"
            @end="endDrag"
          >
            <template #item="{ element, index }">
              <div :class="bem.e('item')">
                <div :class="bem.e('left-info')">
                  <!-- 拖拽图标 -->
                  <div :class="['handle', bem.e('drag-icon')]">
                    <ll-icon icon-name="icon-drag" />
                  </div>

                  <div :class="bem.e('text')">{{ element.title }}</div>
                </div>

                <div :class="bem.e('right-icon')">
                  <!-- 脱敏 -->
                  <ll-icon
                    v-if="!!element.isSensitive"
                    icon-name="icon-pwd-look"
                    :size="16"
                    :class="bem.e('isSensitive-icon')"
                  />

                  <!-- 固定 -->
                  <ll-icon
                    v-if="!!element.isFixed"
                    icon-name="icon-lock1"
                    :size="16"
                    :class="bem.e('isFixed-icon')"
                  />
                  <!-- 移除图标 -->
                  <ll-icon
                    v-else-if="enableAddField"
                    icon-name="icon-remove"
                    :class="bem.e('remove-icon')"
                    :size="16"
                    @click="handleRemove(index)"
                  />
                </div>
              </div>
            </template>
          </draggable>

          <ll-empty
            v-if="columnList.length === 0"
            :style="{ margin: '12px 0' }"
            image-width="80px"
          />

          <div v-if="enableAddField" :class="bem.e('operate')">
            <a-popover
              trigger="click"
              placement="right"
              :arrow="false"
              :overlay-class-name="subBem.b()"
            >
              <ll-button
                :class="bem.e('operate-btn')"
                block
                use-custom-style
                :icon-space="8"
              >
                <ll-icon icon-name="icon-add" />
                {{ addFieldText || '添加字段' }}
              </ll-button>
              <template #content>
                <div :class="subBem.e('content')">
                  <ll-input
                    v-model:value.trim="searchVal"
                    :class="subBem.e('input')"
                    is-gray
                    size="small"
                    placeholder="搜索"
                  >
                    <template #prefix>
                      <ll-icon icon-name="icon-search" />
                    </template>
                  </ll-input>
                  <div :class="subBem.e('list')">
                    <div
                      v-for="field in filterField"
                      :key="field.key"
                      :class="[
                        subBem.e('item'),
                        bem.is(
                          columnList.some((i) => i.key === field.key)
                            ? 'active'
                            : ''
                        ),
                        bem.is(
                          maxCount &&
                            columnList.length >= maxCount &&
                            !columnList.some((i) => i.key === field.key)
                            ? 'disabled'
                            : ''
                        ),
                      ]"
                      @click="handleClick(field)"
                    >
                      <span :class="subBem.e('text')"> {{ field.title }}</span>
                      <ll-icon
                        v-if="columnList.some((i) => i.key === field.key)"
                        icon-name="icon-check"
                        color="inherit"
                        :size="16"
                      />
                    </div>
                  </div>
                  <ll-empty
                    v-if="filterField.length === 0"
                    :style="{ margin: '0 0 12px 0' }"
                    image-width="80px"
                  />
                </div>
              </template>
            </a-popover>
          </div>
        </a-form-item-rest>
      </div>
    </template>
  </a-popover>
</template>

<script lang="ts" setup>
import { addUnit, createNamespace } from '@ll-plus/utils'
import { computed, onMounted, ref, useAttrs, watch } from 'vue'
import { columnManagerFieldItem, columnManagerProps } from './column-manager'

import draggable from 'vuedraggable'

defineOptions({ name: 'LlColumnManager' })

const props = defineProps(columnManagerProps)
const emits = defineEmits([
  'update:open',
  'change',
  'init',
  'start',
  'update',
  'end',
])

const bem = createNamespace('column-manager')
const subBem = createNamespace('column-manager-sub')

const attrs = useAttrs()

const localList = computed(() => [...props.fieldList])
const columnList = ref([] as columnManagerFieldItem[])
const searchVal = ref('')

const newOpen = computed({
  get() {
    return props.open
  },
  set(open: boolean) {
    emits('update:open', open)
  },
})

const filterField = computed(() => {
  return localList.value.filter(
    (field) =>
      !field.isHidden &&
      // 查询匹配title 和 key
      (field.title.includes(searchVal.value.toLowerCase()) ||
        field.key.includes(searchVal.value.toLowerCase()))
  )
})

const startDrag = (e: any) => {
  emits('start', e, columnList.value)
}

const updateDrag = (e: any) => {
  emits('update', e, columnList.value)
}
const endDrag = (e: any) => {
  emits('end', e, columnList.value)
}

// 选中列字段
const handleClick = (item: columnManagerFieldItem) => {
  // 已选择则返回
  if (columnList.value.some((i) => i.key === item.key)) return

  // 最大展示数返回
  if (!!props.maxCount && props.maxCount <= columnList.value.length) return

  columnList.value.push(item)
  emits('change', columnList.value)
}

// 移除
const handleRemove = (idx: number) => {
  columnList.value.splice(idx, 1)
  emits('change', columnList.value)
}

watch(
  () => columnList.value,
  (list) => {
    // 若开启本地存储则存入
    if (props.useLocalStorage && props.localKey) {
      localStorage.setItem(props.localKey, JSON.stringify(list))

      // 若列表没值则清除对应的本地存储
      if (list.length === 0) {
        localStorage.removeItem(props.localKey)
      }
    }
  },
  {
    deep: true,
  }
)

watch(
  () => props.useLocalStorage,
  (v) => {
    if (v && props.localKey && columnList.value.length > 0) {
      localStorage.setItem(props.localKey, JSON.stringify(columnList.value))
    } else if (!v && props.localKey) {
      localStorage.removeItem(props.localKey)
    }
  }
)

watch(
  () => props.fieldList,
  () => {
    // 固定和初始显示都加入初始弹窗
    columnList.value = localList.value.filter(
      (i) => !i.isHidden && (i.isFixed || i.initialDisplay)
    )
    emits('init', columnList.value)
  },
  { deep: true }
)

// 初始化
onMounted(() => {
  // 备选值
  const alternative = localList.value.filter(
    (i) => !i.isHidden && (i.isFixed || i.initialDisplay)
  )

  // 若开启本地存储且key有值
  if (props.useLocalStorage && props.localKey) {
    const data = localStorage.getItem(props.localKey)
    // 当前本地存储有值
    if (data) {
      try {
        const result = JSON.parse(data)
        columnList.value = Array.isArray(result) ? result : alternative
      } catch {
        columnList.value = alternative
      }
    } else {
      columnList.value = alternative
    }
  }
  // 取固定值
  else {
    columnList.value = alternative
  }
  emits('init', columnList.value)
})
</script>
